"use client"

import { Button } from "@/components/ui/button"
import { AlertTriangle, Home, RotateCcw } from "lucide-react"
import { useEffect } from "react"

type ErrorProps = {
	error: Error & { digest?: string }
	reset: () => void
}

export default function AppError({ error, reset }: ErrorProps) {
	useEffect(() => {
		console.error("App error:", error)
	}, [error])

	return (
		<div className="min-h-screen flex items-center justify-center p-6 relative overflow-hidden">
			{/* Scan line effect - rose tinted */}
			<div
				className="pointer-events-none fixed inset-0 z-10"
				style={{
					background:
						"repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(244, 63, 94, 0.03) 2px, rgba(244, 63, 94, 0.03) 4px)",
				}}
			/>

			{/* Red radial glow for error state */}
			<div
				className="pointer-events-none fixed inset-0"
				style={{
					background:
						"radial-gradient(ellipse at center, rgba(244, 63, 94, 0.08) 0%, transparent 60%)",
				}}
			/>

			<div className="relative z-20 text-center max-w-lg">
				{/* Error code display */}
				<div className="mb-8">
					<div className="inline-flex items-center gap-3 px-4 py-2 rounded-full border border-border bg-card/50 mb-6">
						<span className="status-dot status-dot-down animate-glow-pulse" />
						<span className="font-mono text-xs text-muted-foreground tracking-wider uppercase">
							Error Detected
						</span>
					</div>

					<h1 className="font-mono text-[8rem] leading-none font-bold tracking-tighter select-none text-destructive/20 text-glow-rose">
						500
					</h1>
				</div>

				{/* Diagnostic panel */}
				<div className="bg-card border border-border rounded-xl p-6 mb-8 text-left">
					<div className="flex items-center gap-2 mb-4 pb-3 border-b border-border">
						<AlertTriangle className="h-4 w-4 text-muted-foreground" />
						<span className="font-mono text-xs text-muted-foreground">
							EXCEPTION
						</span>
					</div>

					<div className="space-y-3 font-mono text-sm">
						<div className="flex justify-between">
							<span className="text-muted-foreground">Status</span>
							<span className="text-destructive">INTERNAL_ERROR</span>
						</div>
						<div className="flex justify-between">
							<span className="text-muted-foreground">Type</span>
							<span className="text-foreground">Unhandled Exception</span>
						</div>
						{error.digest && (
							<div className="flex justify-between">
								<span className="text-muted-foreground">Digest</span>
								<span className="text-muted-foreground/60 font-mono">
									{error.digest}
								</span>
							</div>
						)}
					</div>
				</div>

				<p className="text-muted-foreground mb-8 text-sm">
					Something went wrong. Our team has been notified. Please try again or
					return home.
				</p>

				<div className="flex gap-3 justify-center">
					<Button variant="outline" onClick={reset}>
						<RotateCcw className="mr-2 h-4 w-4" />
						Try again
					</Button>
					<Button asChild className="glow-button">
						<a href="/">
							<Home className="mr-2 h-4 w-4" />
							Home
						</a>
					</Button>
				</div>
			</div>
		</div>
	)
}
